<template>
  <div class="ejercicios-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="ejercicios-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-weight-lifter</v-icon>
          </div>
          <div>
            <h1 class="page-title">Biblioteca de ejercicios</h1>
            <p class="page-sub">Gestiona tu catálogo completo de ejercicios</p>
          </div>
        </div>
        <button class="btn-primary" @click="mostrarDialogo = true">
          <v-icon size="15" class="mr-2">mdi-plus</v-icon>
          Nuevo ejercicio
        </button>
      </header>

      <!-- Stats -->
      <section class="dash-section fade-in delay-100">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon stat-icon-accent">
                <v-icon size="18" color="#0E0E10">mdi-weight-lifter</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ ejercicios.length }}</div>
            <div class="stat-label">Total ejercicios</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-chart-line</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ ejerciciosPrincipiante }}</div>
            <div class="stat-label">Principiante</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-chart-bell-curve</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ ejerciciosIntermedio }}</div>
            <div class="stat-label">Intermedio</div>
          </div>

          <div class="stat-card">
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">mdi-fire</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ ejerciciosAvanzado }}</div>
            <div class="stat-label">Avanzado</div>
          </div>
        </div>
      </section>

      <!-- Filtros -->
      <section class="dash-section fade-in delay-200">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Filtros y búsqueda
            </span>
          </div>

          <div class="card-flat-body">
            <div class="search-input-wrapper mb-lg">
              <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
              <input v-model="search" type="text" class="search-input" placeholder="Buscar ejercicios…" />
              <button v-if="search" class="clear-btn" @click="search = ''">
                <v-icon size="14" color="#5C5C62">mdi-close</v-icon>
              </button>
            </div>

            <div class="mb-lg">
              <label class="field-label mb-sm d-block">Categorías</label>
              <div class="chip-row">
                <button
                  class="filter-chip"
                  :class="{ 'filter-chip-active': categoriaFiltro === null }"
                  @click="categoriaFiltro = null"
                >
                  Todas las categorías
                </button>
                <button
                  v-for="category in categorias"
                  :key="category.id"
                  class="filter-chip"
                  :class="{ 'filter-chip-active': categoriaFiltro === category.id }"
                  @click="categoriaFiltro = category.id"
                >
                  <v-icon size="13" class="mr-1">{{ category.icono || 'mdi-dumbbell' }}</v-icon>
                  {{ category.nombre }}
                </button>
              </div>
            </div>

            <div class="field-row">
              <div class="field">
                <label class="field-label">Nivel de dificultad</label>
                <v-select
                  v-model="filtros.dificultad"
                  :items="nivelesDificultad"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Todos los niveles"
                  class="dark-input"
                ></v-select>
              </div>
              <div class="field">
                <label class="field-label">Equipamiento</label>
                <v-select
                  v-model="filtros.equipamiento"
                  :items="tiposEquipamiento"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Todos los equipamientos"
                  class="dark-input"
                ></v-select>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Tabla -->
      <section class="dash-section fade-in delay-300">
        <div class="card-flat card-flat-nopad">
          <div class="table-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Listado de ejercicios
              <span class="count-text">({{ ejerciciosFiltrados.length }})</span>
            </span>
          </div>

          <v-data-table
            :headers="encabezados"
            :items="ejerciciosFiltrados"
            sort-by="nombre"
            no-data-text="No hay ejercicios registrados"
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.nombre`]="{ item }">
              <span class="cell-strong">{{ item.nombre }}</span>
            </template>

            <template v-slot:[`item.categoria`]="{ item }">
              <span class="status-tag status-tag-neutral">{{ obtenerNombreCategoria(item.categoria_id) }}</span>
            </template>

            <template v-slot:[`item.nivel_dificultad`]="{ item }">
              <span v-if="item.nivel_dificultad" class="nivel-badge" :class="`nivel-${item.nivel_dificultad}`">
                {{ item.nivel_dificultad }}
              </span>
              <span v-else class="cell-muted">No especificado</span>
            </template>

            <template v-slot:[`item.met`]="{ item }">
              <span v-if="item.met" class="status-tag status-tag-success">{{ item.met }}</span>
              <span v-else class="cell-muted">N/A</span>
            </template>

            <template v-slot:[`item.equipamiento`]="{ item }">
              <span v-if="item.equipamiento" class="cell-secondary">{{ item.equipamiento }}</span>
              <span v-else class="cell-muted">No especificado</span>
            </template>

            <template v-slot:[`item.opciones`]="{ item }">
              <div class="flex gap-xs">
                <button class="icon-btn" @click="verDetalles(item)" title="Ver detalles">
                  <v-icon size="15" color="#8C8C93">mdi-eye</v-icon>
                </button>
                <button class="icon-btn" @click="editar(item)" title="Editar">
                  <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                </button>
                <button class="icon-btn icon-btn-danger" @click="eliminar(item)" title="Eliminar">
                  <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                </button>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-dumbbell</v-icon>
                </div>
                <h3 class="empty-title">No hay ejercicios registrados</h3>
                <p class="empty-desc">Comienza agregando ejercicios a tu biblioteca</p>
                <button class="btn-primary mt-lg" @click="mostrarDialogo = true">
                  <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                  Agregar primer ejercicio
                </button>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando ejercicios…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </div>

    <!-- FAB -->
    <button class="fab-button" @click="mostrarDialogo = true">
      <v-icon size="22" color="#0E0E10">mdi-plus</v-icon>
    </button>

    <!-- Diálogo agregar/editar -->
    <v-dialog v-model="mostrarDialogo" persistent max-width="800px">
      <form-ejercicio @cerrado="onCerrado" @registrar="onRegistrar" :ejercicio="ejercicioEditar"
        :titulo="tituloDialogo" :categorias="categorias" />
    </v-dialog>

    <!-- Diálogo eliminar -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="480px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">¿Eliminar ejercicio?</h3>
        </div>

        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar el ejercicio
            <strong class="text-error">"{{ itemSeleccionado }}"</strong>?
          </p>
          <div class="alert-box">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">
              Esta acción no se puede deshacer y afectará a las rutinas que lo utilicen.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogoEliminar">Cancelar</button>
          <button class="btn-danger-solid" @click="confirmarEliminar" :disabled="cargando">
            <span v-if="!cargando">Eliminar</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Eliminando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Diálogo detalles -->
    <v-dialog v-model="mostrarDialogoDetalles" max-width="760px">
      <div class="modal-panel" v-if="ejercicioSeleccionado">
        <div class="detail-image-wrapper">
          <img :src="ejercicioSeleccionado.imagen_url || 'https://via.placeholder.com/800x400?text=Ejercicio'"
            alt="Ejercicio" />
        </div>

        <div class="modal-head detail-head">
          <div>
            <h3 class="modal-title">{{ ejercicioSeleccionado.nombre }}</h3>
            <span v-if="ejercicioSeleccionado.nivel_dificultad" class="nivel-badge mt-sm"
              :class="`nivel-${ejercicioSeleccionado.nivel_dificultad}`">
              {{ ejercicioSeleccionado.nivel_dificultad }}
            </span>
          </div>
        </div>

        <div class="modal-body">
          <div class="detail-grid">
            <div>
              <span class="mini-eyebrow">Descripción</span>
              <p class="detail-paragraph">{{ ejercicioSeleccionado.descripcion || 'Sin descripción' }}</p>

              <span class="mini-eyebrow">Instrucciones</span>
              <p class="detail-paragraph">{{ ejercicioSeleccionado.instrucciones || 'Sin instrucciones' }}</p>
            </div>

            <div>
              <span class="mini-eyebrow">Detalles</span>
              <div class="info-table">
                <div class="info-row">
                  <span class="info-key">Categoría</span>
                  <span class="info-val">{{ obtenerNombreCategoria(ejercicioSeleccionado.categoria_id) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Nivel</span>
                  <span class="info-val">{{ ejercicioSeleccionado.nivel_dificultad || 'No especificado' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Equipamiento</span>
                  <span class="info-val">{{ ejercicioSeleccionado.equipamiento || 'No especificado' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">MET</span>
                  <span class="info-val">{{ ejercicioSeleccionado.met || 'N/A' }}</span>
                </div>
              </div>

              <a v-if="ejercicioSeleccionado.video_url" :href="ejercicioSeleccionado.video_url" target="_blank"
                class="btn-primary video-link mt-lg">
                Ver video
                <v-icon size="14" class="ml-2">mdi-open-in-new</v-icon>
              </a>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="mostrarDialogoDetalles = false">Cerrar</button>
          <button class="btn-warning-solid" @click="editar(ejercicioSeleccionado)">
            <v-icon size="15" class="mr-2">mdi-pencil</v-icon>
            Editar
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Loading Overlay -->
    <div v-if="cargando && ejercicios.length === 0" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando ejercicios…</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import FormEjercicio from './FormEjercicio.vue'
import DialogoEliminar from '../Dialogos/DialogoEliminar.vue'
import HttpService from '../../Servicios/HttpService'

export default {
  name: "Ejercicios",
  components: {
    FormEjercicio,
    DialogoEliminar
  },

  data: () => ({
    cargando: false,
    mostrarDialogo: false,
    mostrarDialogoEliminar: false,
    mostrarDialogoDetalles: false,
    ejercicioEditar: {
      id: null,
      nombre: "",
      descripcion: "",
      instrucciones: "",
      categoria_id: null,
      imagen_url: "",
      video_url: "",
      met: null,
      nivel_dificultad: null,
      equipamiento: null
    },
    ejercicioSeleccionado: null,
    ejercicios: [],
    categorias: [],
    mensaje: {
      texto: "",
      color: ""
    },
    mostrarMensaje: false,
    encabezados: [
      { text: "Nombre", value: "nombre", sortable: true },
      { text: "Categoría", value: "categoria", sortable: true },
      { text: "Dificultad", value: "nivel_dificultad", sortable: true },
      { text: "MET", value: "met", sortable: true },
      { text: "Equipamiento", value: "equipamiento", sortable: true },
      { text: 'Opciones', value: 'opciones', sortable: false, width: '150px' },
    ],
    tituloDialogo: "Agregar",
    itemSeleccionado: "",
    search: "",
    categoriaFiltro: null,
    filtros: {
      dificultad: null,
      equipamiento: null
    },
    nivelesDificultad: ['principiante', 'intermedio', 'avanzado'],
    tiposEquipamiento: ['peso corporal', 'mancuernas', 'barra', 'kettlebell', 'bandas elásticas', 'máquina', 'balón medicinal', 'steps']
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        default: return 'mdi-information'
      }
    },

    ejerciciosPrincipiante() {
      return this.ejercicios.filter(e => e.nivel_dificultad === 'principiante').length
    },

    ejerciciosIntermedio() {
      return this.ejercicios.filter(e => e.nivel_dificultad === 'intermedio').length
    },

    ejerciciosAvanzado() {
      return this.ejercicios.filter(e => e.nivel_dificultad === 'avanzado').length
    },

    ejerciciosFiltrados() {
      let filtered = this.ejercicios;

      if (this.categoriaFiltro !== null) {
        filtered = filtered.filter(ejercicio => ejercicio.categoria_id === this.categoriaFiltro);
      }

      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(ejercicio =>
          ejercicio.nombre.toLowerCase().includes(searchLower) ||
          (ejercicio.descripcion && ejercicio.descripcion.toLowerCase().includes(searchLower))
        );
      }

      if (this.filtros.dificultad) {
        filtered = filtered.filter(ejercicio =>
          ejercicio.nivel_dificultad === this.filtros.dificultad
        );
      }

      if (this.filtros.equipamiento) {
        filtered = filtered.filter(ejercicio =>
          ejercicio.equipamiento === this.filtros.equipamiento
        );
      }

      return filtered;
    }
  },

  mounted() {
    this.obtenerEjercicios();
    this.obtenerCategorias();
  },

  methods: {
    async obtenerEjercicios() {
      this.cargando = true;
      let payload = { metodo: "get" };

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "ejercicios.php");
        this.ejercicios = respuesta;
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al cargar los ejercicios",
          color: "error"
        };
        console.error("Error al obtener ejercicios:", error);
      } finally {
        this.cargando = false;
      }
    },

    async obtenerCategorias() {
      let payload = { metodo: "get" };

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "categorias_ejercicios.php");
        this.categorias = respuesta;
      } catch (error) {
        console.error("Error al obtener categorías:", error);
      }
    },

    editar(ejercicio) {
      this.tituloDialogo = "Editar";
      this.ejercicioEditar = { ...ejercicio };
      this.mostrarDialogo = true;
      this.mostrarDialogoDetalles = false;
    },

    eliminar(ejercicio) {
      this.itemSeleccionado = ejercicio.nombre;
      this.ejercicioEditar = { ...ejercicio };
      this.mostrarDialogoEliminar = true;
    },

    verDetalles(ejercicio) {
      this.ejercicioSeleccionado = { ...ejercicio };
      this.mostrarDialogoDetalles = true;
    },

    cerrarDialogoEliminar() {
      this.mostrarDialogoEliminar = false;
      this.ejercicioEditar = {
        id: null,
        nombre: "",
        descripcion: "",
        instrucciones: "",
        categoria_id: null,
        imagen_url: "",
        video_url: "",
        met: null,
        nivel_dificultad: null,
        equipamiento: null
      };
    },

    async confirmarEliminar() {
      this.cargando = true;
      let payload = {
        metodo: "delete",
        id: this.ejercicioEditar.id
      };

      try {
        const eliminado = await HttpService.eliminar("ejercicios.php", payload);

        if (eliminado) {
          this.mostrarDialogoEliminar = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Ejercicio eliminado",
            color: "success"
          };
          await this.obtenerEjercicios();
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al eliminar el ejercicio",
          color: "error"
        };
        console.error("Error al eliminar:", error);
      } finally {
        this.cargando = false;
      }
    },

    onCerrado(valor) {
      this.ejercicioEditar = {
        id: null,
        nombre: "",
        descripcion: "",
        instrucciones: "",
        categoria_id: null,
        imagen_url: "",
        video_url: "",
        met: null,
        nivel_dificultad: null,
        equipamiento: null
      };
      this.mostrarDialogo = valor;
      this.tituloDialogo = "Agregar";
    },

    async onRegistrar(ejercicio) {
      let metodo = (this.tituloDialogo === "Agregar") ? "post" : "put";
      this.ejercicioEditar = ejercicio;
      this.cargando = true;

      let payload = {
        metodo: metodo,
        ejercicio: this.ejercicioEditar
      };

      try {
        const respuesta = await HttpService.registrar(payload, "ejercicios.php");

        if (respuesta) {
          this.ejercicioEditar = {
            id: null,
            nombre: "",
            descripcion: "",
            instrucciones: "",
            categoria_id: null,
            imagen_url: "",
            video_url: "",
            met: null,
            nivel_dificultad: null,
            equipamiento: null
          };
          this.mostrarDialogo = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Ejercicio registrado",
            color: "success"
          };
          await this.obtenerEjercicios();
          this.tituloDialogo = "Agregar";
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al registrar el ejercicio",
          color: "error"
        };
        console.error("Error al registrar:", error);
      } finally {
        this.cargando = false;
      }
    },

    obtenerNombreCategoria(categoriaId) {
      const categoria = this.categorias.find(c => c.id === categoriaId);
      return categoria ? categoria.nombre : 'Sin categoría';
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.ejercicios-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.ejercicios-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.ejercicios-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== SECTIONS ===== */
.dash-section {
  margin-bottom: 20px;
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.count-text {
  color: var(--text-tertiary);
  text-transform: none;
  letter-spacing: 0;
}

/* ===== STATS ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}

.stat-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px;
}

.stat-top {
  margin-bottom: 14px;
}

.stat-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon-accent {
  background: var(--accent);
  border-color: var(--accent);
}

.stat-value {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.card-flat-head,
.table-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.card-flat-body {
  padding: 22px;
}

/* ===== SEARCH ===== */
.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
}

/* ===== CHIPS ===== */
.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.chip-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 13px;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 12.5px;
  font-family: var(--font-body);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.filter-chip:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.filter-chip-active {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-dim);
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ===== TABLE CELLS ===== */
.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.cell-muted {
  font-size: 13px;
  color: var(--text-tertiary);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  white-space: nowrap;
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

/* Nivel de dificultad: semántico de 3 niveles */
.nivel-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.nivel-principiante {
  background: var(--accent-dim);
  color: var(--accent);
}

.nivel-intermedio {
  background: rgba(255, 184, 77, 0.12);
  color: var(--warning);
}

.nivel-avanzado {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== FAB ===== */
.fab-button {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.fab-button:hover {
  transform: scale(1.06);
  filter: brightness(1.05);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  text-decoration: none;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-warning-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--warning);
  color: #f6f6f8;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-warning-solid:hover {
  filter: brightness(1.08);
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #ff0000;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-danger-solid:disabled {
  opacity: 0.6;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.detail-head {
  border-top: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0 0 18px;
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== DETALLE ===== */
.detail-image-wrapper {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.detail-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}

.mini-eyebrow {
  display: block;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 8px;
}

.detail-paragraph {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 18px;
}

.info-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-key {
  font-size: 12px;
  color: var(--text-tertiary);
}

.info-val {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
}

.video-link {
  display: inline-flex;
}

/* ===== Overrides Vuetify (v-select) ===== */
.dark-input >>> .v-input__slot {
  background: var(--bg) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> .v-select__selection {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13.5px !important;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.ejercicios-page >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.ejercicios-page >>> .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.ejercicios-page >>> .v-list-item {
  border-radius: 4px !important;
  text-transform: capitalize;
}

.ejercicios-page >>> .v-list-item__title {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13px !important;
}

.ejercicios-page >>> .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.ejercicios-page >>> .v-list-item--active {
  background: var(--accent-dim) !important;
}

.ejercicios-page >>> .v-list-item--active .v-list-item__title {
  color: var(--accent) !important;
}

/* ===== Overrides v-data-table ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 22px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 22px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 22px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }
.delay-300 { animation-delay: 0.15s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .ejercicios-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .field-row {
    grid-template-columns: 1fr;
  }

  .card-flat-body {
    padding: 16px;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Planes/Ejercicio.vue